import React, { useEffect, useState } from 'react';
import { X, Sliders, CalendarClock, Trash2, Check, Edit3, Layers, Globe, Lock } from 'lucide-react';
import { useBackgroundStore } from './BackgroundProvider';
import BGMiniPreview from './BGMiniPreview';
import { findScheduleDuplicate, validateSchedule, overnightSpan } from './scheduleUtils';
import { toast } from '../../utils/toast';
import { confirmDialog } from "../../components/em/confirm";
import {
  Label,
  Input,
  Select,
  Slider,
  Toggle,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
} from './Primitives';

// Local YYYY-MM-DD (toISOString is UTC and would be a day off after 18:30 IST).
const localToday = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export default function BGEditDrawer({ bg, onClose, onEditScene }) {
  const { state, updateBackground, deleteBackground } = useBackgroundStore();
  const [draft, setDraft] = useState(bg);
  // Set by the first Save attempt — from then on empty fields are flagged too.
  const [attempted, setAttempted] = useState(false);

  useEffect(() => { setDraft(bg); setAttempted(false); }, [bg?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!draft) return null;

  // Same schedule + same priority as another background → duplicate; Save is
  // blocked until the schedule or the priority changes.
  const duplicate = findScheduleDuplicate(draft, state.backgrounds);

  // Date / time validation. "Already passed" only applies when the window was
  // changed here, so an expired background's other settings stay editable.
  const sch = draft.schedule || {};
  const was = bg?.schedule || {};
  const windowChanged = sch.kind !== was.kind || sch.start !== was.start || sch.end !== was.end;
  // Past start dates are refused only when the start itself was picked here —
  // a range that's already running keeps its start and can still be edited.
  const startChanged = sch.kind !== was.kind || sch.start !== was.start;
  const today = localToday();
  const scheduleErrors = validateSchedule(sch, {
    today: windowChanged ? today : undefined,
    startFrom: startChanged ? today : undefined,
  });
  const hasScheduleErrors = Object.keys(scheduleErrors).length > 0;
  // A missing value is only flagged after a Save attempt; a wrong one at once.
  const fieldError = (field) => {
    const msg = scheduleErrors[field];
    if (!msg) return null;
    const filled = String(sch[field] ?? '').trim() !== '';
    return attempted || filled ? msg : null;
  };
  const errorStyle = (field) => (fieldError(field) ? { borderColor: '#ef4444', background: '#fef2f2' } : undefined);
  const renderError = (field) =>
    fieldError(field) ? (
      <div role="alert" style={{ marginTop: 5, color: '#b91c1c', font: "500 11.5px/1.4 'Inter', sans-serif" }}>
        {fieldError(field)}
      </div>
    ) : null;
  // Valid windows that wrap: say so, so they don't look like a mistake.
  const wrapHint =
    sch.kind === 'yearly' && !hasScheduleErrors && sch.end < sch.start
      ? 'Runs across New Year.'
      : null;
  // An overnight time-of-day window (end before start) is allowed, but it is
  // also what a typo looks like (9 PM → 7 PM = 22 hours), so it gets an amber
  // notice with the exact span and a confirm on Save.
  const overnight = !hasScheduleErrors ? overnightSpan(sch) : null;
  // Confirm only when the times were set here — re-saving an existing night
  // background for some other setting shouldn't ask every time.
  const timesChanged =
    sch.kind !== was.kind || sch.timeStart !== was.timeStart || sch.timeEnd !== was.timeEnd;

  const isStatic = draft.kind === 'static';
  const isVideo = draft.kind === 'video';
  const isScene = draft.kind === 'scene';

  const patch = (p) => setDraft((d) => ({ ...d, ...p }));
  const patchSettings = (p) =>
    setDraft((d) => ({ ...d, settings: { ...d.settings, ...p } }));
  const patchSchedule = (p) =>
    setDraft((d) => ({ ...d, schedule: { ...d.schedule, ...p } }));
  // For static: blur/scale/rotate/offsetX/offsetY live on the single layer.
  const patchLayer0 = (p) =>
    setDraft((d) => {
      const layers = (d.layers || []).slice();
      if (!layers[0]) return d;
      layers[0] = { ...layers[0], ...p };
      return { ...d, layers };
    });
  const L0 = draft.layers?.[0] || {};

  const save = async () => {
    if (duplicate) return;
    if (hasScheduleErrors) {
      setAttempted(true);
      toast.error(Object.values(scheduleErrors)[0]);
      return;
    }
    if (overnight && timesChanged) {
      const ok = await confirmDialog(
        `This window runs overnight: ${overnight.label}. ` +
          'The background will show for that whole span. Save anyway?'
      );
      if (!ok) return;
    }
    // Send `shared` only when it actually changed (and may be changed) — it
    // is a sharing decision, not part of every edit.
    const { shared, ...rest } = draft;
    const sharingChanged = bg.canShare && !!shared !== !!bg.shared;
    updateBackground(draft.id, sharingChanged ? { ...rest, shared: !!shared } : rest);
    onClose();
  };
  const del = async () => {
    if (await confirmDialog(`Delete "${draft.name}"?`)) {
      deleteBackground(draft.id);
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1200,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 520,
          maxWidth: '100%',
          height: '100%',
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-20px 0 40px -20px rgba(0,0,0,0.2)',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: '700 18px Inter', color: '#0a0a0a' }}>
              Edit background
            </div>
            <div style={{ font: '400 12px Inter', color: '#4a5565' }}>
              Per-image settings &amp; schedule
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              border: 0,
              background: '#f3f4f6',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 22 }}>
          <BGMiniPreview bg={draft} height={200} />

          <div style={{ marginTop: 18 }}>
            <Label>Name</Label>
            <Input
              value={draft.name}
              onChange={(e) => patch({ name: e.target.value })}
            />
          </div>

          {/* Share with everyone — moved here from the library card. Only on
              backgrounds the server says this user may share (their own
              library upload); applied with Save changes. */}
          {bg.canShare && (
            <div
              style={{
                marginTop: 14, padding: '12px 14px', borderRadius: 10,
                border: '1px solid #e5e7eb', background: '#fafafa',
                display: 'flex', alignItems: 'center', gap: 12,
              }}
            >
              <div
                style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: draft.shared ? '#dcfce7' : '#f3f4f6',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {draft.shared ? <Globe size={15} color="#008236" /> : <Lock size={15} color="#6a7282" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: '600 13px Inter', color: '#0a0a0a' }}>Share with everyone</div>
                <div style={{ font: '400 12px/1.4 Inter', color: '#6a7282', marginTop: 2 }}>
                  {draft.shared
                    ? 'Everyone can see and use it. Only you can edit or delete it.'
                    : 'Private — only you can see it. Share it to set it live or as a default.'}
                </div>
              </div>
              <Toggle
                checked={!!draft.shared}
                onChange={(v) => patch({ shared: v })}
                label="Share with everyone"
              />
            </div>
          )}

          {draft.kind === 'scene' && (
            <div
              style={{
                marginTop: 14,
                padding: '12px 14px',
                borderRadius: 10,
                background:
                  'linear-gradient(135deg,rgba(255,105,0,0.08),rgba(245,73,0,0.04))',
                border: '1px solid rgba(255,105,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg,#ff6900,#f54900)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Layers size={16} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: '600 13px Inter', color: '#0a0a0a' }}>
                  Multi-layer scene · {draft.layers?.length || 0} layers
                </div>
                <div style={{ font: '400 11px Inter', color: '#4a5565' }}>
                  Edit individual layers, depth, and blend modes
                </div>
              </div>
              <SecondaryButton onClick={() => onEditScene && onEditScene(draft.id)}>
                <Edit3 size={14} /> Open scene builder
              </SecondaryButton>
            </div>
          )}

          <div
            style={{
              marginTop: 18,
              paddingTop: 18,
              borderTop: '1px solid #f3f4f6',
            }}
          >
            <div
              style={{
                font: '600 13px Inter',
                color: '#0a0a0a',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Sliders size={14} color="#f54900" /> Appearance
            </div>

            {/* Static image: per-image sliders (blur, scale, rotate, offsets).
                No parallax intensity — static images have no parallax. */}
            {isStatic && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 14,
                  marginBottom: 14,
                }}
              >
                <div>
                  <Label>
                    Blur{' '}
                    <span style={{ color: '#99a1af' }}>{L0.blur || 0}px</span>
                  </Label>
                  <Slider
                    value={L0.blur || 0}
                    min={0}
                    max={30}
                    onChange={(v) => patchLayer0({ blur: v })}
                  />
                </div>
                <div>
                  <Label>
                    Scale{' '}
                    <span style={{ color: '#99a1af' }}>{L0.scale || 100}%</span>
                  </Label>
                  <Slider
                    value={L0.scale || 100}
                    min={50}
                    max={200}
                    onChange={(v) => patchLayer0({ scale: v })}
                  />
                </div>
                <div>
                  <Label>
                    Rotate{' '}
                    <span style={{ color: '#99a1af' }}>{L0.rotate || 0}°</span>
                  </Label>
                  <Slider
                    value={L0.rotate || 0}
                    min={-180}
                    max={180}
                    onChange={(v) => patchLayer0({ rotate: v })}
                  />
                </div>
                <div />
                <div>
                  <Label>
                    Offset X{' '}
                    <span style={{ color: '#99a1af' }}>
                      {L0.offsetX || 0}px
                    </span>
                  </Label>
                  <Slider
                    value={L0.offsetX || 0}
                    min={-200}
                    max={200}
                    onChange={(v) => patchLayer0({ offsetX: v })}
                  />
                </div>
                <div>
                  <Label>
                    Offset Y{' '}
                    <span style={{ color: '#99a1af' }}>
                      {L0.offsetY || 0}px
                    </span>
                  </Label>
                  <Slider
                    value={L0.offsetY || 0}
                    min={-200}
                    max={200}
                    onChange={(v) => patchLayer0({ offsetY: v })}
                  />
                </div>
              </div>
            )}

            {isVideo && (
              <div
                style={{
                  padding: '10px 12px',
                  marginBottom: 14,
                  borderRadius: 8,
                  background: '#f9fafb',
                  border: '1px solid #f3f4f6',
                  font: '400 12px/1.5 Inter',
                  color: '#4a5565',
                }}
              >
                {draft.mediaType === 'gif' ? 'Animated GIF' : 'Looping video'}
                {' '}backgrounds have no per-media settings — only Dim, Overlay,
                and Schedule below apply.
              </div>
            )}

            {/* Scene: legacy appearance stack kept (scale + parallax). */}
            {isScene && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 14,
                  marginBottom: 14,
                }}
              >
                <div>
                  <Label>
                    Parallax intensity{' '}
                    <span style={{ color: '#99a1af' }}>
                      {draft.settings.parallax}%
                    </span>
                  </Label>
                  <Slider
                    value={draft.settings.parallax}
                    min={0}
                    max={200}
                    onChange={(v) => patchSettings({ parallax: v })}
                  />
                </div>
                <div>
                  <Label>
                    Scale{' '}
                    <span style={{ color: '#99a1af' }}>
                      {draft.settings.scale}%
                    </span>
                  </Label>
                  <Slider
                    value={draft.settings.scale}
                    min={100}
                    max={130}
                    onChange={(v) => patchSettings({ scale: v })}
                  />
                </div>
              </div>
            )}

            {/* Dim + overlay apply to every kind. */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 14,
              }}
            >
              <div>
                <Label>
                  Dim{' '}
                  <span style={{ color: '#99a1af' }}>{draft.settings.dim}%</span>
                </Label>
                <Slider
                  value={draft.settings.dim}
                  min={0}
                  max={80}
                  onChange={(v) => patchSettings({ dim: v })}
                />
              </div>
              <div>
                <Label>Overlay color</Label>
                <input
                  type="color"
                  value={draft.settings.overlayColor}
                  onChange={(e) =>
                    patchSettings({ overlayColor: e.target.value })
                  }
                  style={{
                    width: '100%',
                    height: 34,
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 2,
                    background: '#fff',
                    cursor: 'pointer',
                  }}
                />
              </div>
              <div>
                <Label>
                  Overlay opacity{' '}
                  <span style={{ color: '#99a1af' }}>
                    {draft.settings.overlayAlpha}%
                  </span>
                </Label>
                <Slider
                  value={draft.settings.overlayAlpha}
                  min={0}
                  max={80}
                  onChange={(v) => patchSettings({ overlayAlpha: v })}
                />
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 18,
              paddingTop: 18,
              borderTop: '1px solid #f3f4f6',
            }}
          >
            <div
              style={{
                font: '600 13px Inter',
                color: '#0a0a0a',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <CalendarClock size={14} color="#f54900" /> Schedule
            </div>
            <Label>Type</Label>
            <Select
              value={draft.schedule.kind}
              onChange={(e) => patchSchedule({ kind: e.target.value })}
            >
              <option value="library">Library only (manual pick)</option>
              <option value="default">Always-available default</option>
              <option value="once">One-time date range</option>
              <option value="yearly">Recurring yearly (holiday)</option>
              <option value="timeOfDay">Time-of-day window</option>
            </Select>
            {draft.schedule.kind === 'once' && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                  marginTop: 12,
                }}
              >
                <div>
                  <Label>Start date</Label>
                  <Input
                    type="date"
                    value={draft.schedule.start || ''}
                    // The picker greys out days before today.
                    min={today}
                    onChange={(e) => patchSchedule({ start: e.target.value })}
                    aria-invalid={!!fieldError('start')}
                    style={errorStyle('start')}
                  />
                  {renderError('start')}
                </div>
                <div>
                  <Label>End date</Label>
                  <Input
                    type="date"
                    value={draft.schedule.end || ''}
                    // The picker greys out days before today and before the
                    // start date, whichever is later.
                    min={draft.schedule.start && draft.schedule.start > today ? draft.schedule.start : today}
                    onChange={(e) => patchSchedule({ end: e.target.value })}
                    aria-invalid={!!fieldError('end')}
                    style={errorStyle('end')}
                  />
                  {renderError('end')}
                </div>
              </div>
            )}
            {draft.schedule.kind === 'yearly' && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                  marginTop: 12,
                }}
              >
                <div>
                  <Label>Start (MM-DD)</Label>
                  <Input
                    placeholder="12-20"
                    maxLength={5}
                    value={draft.schedule.start || ''}
                    onChange={(e) => patchSchedule({ start: e.target.value })}
                    aria-invalid={!!fieldError('start')}
                    style={errorStyle('start')}
                  />
                  {renderError('start')}
                </div>
                <div>
                  <Label>End (MM-DD)</Label>
                  <Input
                    placeholder="12-26"
                    maxLength={5}
                    value={draft.schedule.end || ''}
                    onChange={(e) => patchSchedule({ end: e.target.value })}
                    aria-invalid={!!fieldError('end')}
                    style={errorStyle('end')}
                  />
                  {renderError('end')}
                </div>
              </div>
            )}
            {draft.schedule.kind === 'timeOfDay' && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                  marginTop: 12,
                }}
              >
                <div>
                  <Label>Start time</Label>
                  <Input
                    type="time"
                    value={draft.schedule.timeStart || ''}
                    onChange={(e) => patchSchedule({ timeStart: e.target.value })}
                    aria-invalid={!!fieldError('timeStart')}
                    style={errorStyle('timeStart')}
                  />
                  {renderError('timeStart')}
                </div>
                <div>
                  <Label>End time</Label>
                  <Input
                    type="time"
                    value={draft.schedule.timeEnd || ''}
                    onChange={(e) => patchSchedule({ timeEnd: e.target.value })}
                    aria-invalid={!!fieldError('timeEnd')}
                    style={errorStyle('timeEnd')}
                  />
                  {renderError('timeEnd')}
                </div>
              </div>
            )}
            {wrapHint && (
              <div style={{ marginTop: 6, color: '#6b7280', font: "400 11.5px/1.4 'Inter', sans-serif" }}>
                {wrapHint}
              </div>
            )}
            {draft.schedule.kind === 'timeOfDay' && overnight && (
              <div
                role="status"
                style={{
                  marginTop: 8, padding: '7px 10px', borderRadius: 8,
                  background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e',
                  font: "500 11.5px/1.4 'Inter', sans-serif",
                }}
              >
                Runs overnight: {overnight.label}. Check the end time if you didn't mean to cross midnight.
              </div>
            )}
            <div style={{ marginTop: 14 }}>
              <Label hint="higher wins if schedules overlap">
                Priority{' '}
                <span style={{ color: '#99a1af' }}>{draft.priority}</span>
              </Label>
              <Slider
                value={draft.priority || 0}
                min={0}
                max={100}
                onChange={(v) => patch({ priority: v })}
              />
            </div>
            {duplicate && (
              <div
                role="alert"
                style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #fecaca',
                  background: '#fef2f2',
                  color: '#b91c1c',
                  font: "500 12.5px/1.45 'Inter', sans-serif",
                }}
              >
                Duplicate schedule — <b>{duplicate.name || 'another background'}</b> already
                uses this schedule with priority {Number(draft.priority) || 0}. Change the
                dates/times or the priority to save.
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            gap: 10,
          }}
        >
          <GhostButton style={{ color: '#e7000b' }} onClick={del}>
            <Trash2 size={14} /> Delete
          </GhostButton>
          <div style={{ flex: 1 }} />
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={save} disabled={!!duplicate}>
            <Check size={16} /> Save changes
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
